import React from 'react';
import PropTypes from 'prop-types';
import {
  Container, Message, Segment, Dimmer, Loader, Header,
} from 'semantic-ui-react';
import VinSearchForm from './VinSearchForm';
import TransactionList from './TransactionList';
import EligibilityResultsLog from './EligibilityResultsLog';
import ForceConfirmationModal from './ForceConfirmationModal';
import ForceResultMessage from './ForceResultMessage';
import ForceGuaranteeInstructions from './ForceGuaranteeInstructions';
import {
  SEARCHING_TRANSACTIONS_MESSAGE,
  RUNNING_ELIGIBILITY_MESSAGE,
} from '../../constants/forceGuarantee';

// Force Guarantee page — presentational only. The steps render top-to-bottom
// and appear as their prerequisites are met; all state and orchestration live
// in `containers/ForceGuarantee`.
function ForceGuaranteePage({
  products,
  productsLoading,
  productsErrorMessage,
  transactions,
  transactionsLoading,
  transactionsErrorMessage,
  selectedId,
  productCode,
  vin,
  hasSearched,
  evaluatedId,
  eligibilityResults,
  evaluationLoading,
  evaluationErrorMessage,
  evaluatedVin,
  productName,
  failedRulesCount,
  totalRulesCount,
  forceResult,
  forceLoading,
  forceErrorMessage,
  modalOpen,
  busy,
  canForce,
  onSearch,
  onSelect,
  onRunEligibility,
  onOpenModal,
  onCloseModal,
  onConfirm,
  onBack,
  onReset,
}) {
  return (
    <div>
      <Container>
        <Segment.Group>
          <Segment>
            <Dimmer active={transactionsLoading} inverted>
              <Loader active inline='centered'>
                {SEARCHING_TRANSACTIONS_MESSAGE}
              </Loader>
            </Dimmer>
            <VinSearchForm
              loading={transactionsLoading}
              disabled={busy}
              products={products}
              productsLoading={productsLoading}
              searchProductCode={productCode}
              searchVin={vin}
              onSearch={onSearch}
            />
            {productsErrorMessage && (
              <Message negative>
                <p>{productsErrorMessage}</p>
              </Message>
            )}
            {transactionsErrorMessage && (
              <Message negative>
                <p>{transactionsErrorMessage}</p>
              </Message>
            )}
          </Segment>

          {/* Shown only after the search response arrives — while transactions
              are loading we display just the loader above. An empty result
              still renders, so the user sees that the lookup completed and
              simply found nothing.

              Replaced by the eligibility log as soon as results arrive: the two
              steps are mutually exclusive, so the operator cannot re-run the
              rules or pick another row while an evaluation (or a created
              guarantee) is on screen. "Back" clears the evaluation and brings
              the list back. A failed run keeps the list, so it can be retried
              without stepping back. */}
          {hasSearched && !transactionsLoading && !transactionsErrorMessage && !eligibilityResults && (
            <Segment>
              <Dimmer active={evaluationLoading} inverted>
                <Header as='h3'>
                  <Loader active inline='centered' size='large' />
                  <br />
                  {RUNNING_ELIGIBILITY_MESSAGE}
                </Header>
              </Dimmer>
              <TransactionList
                transactions={transactions}
                selectedId={selectedId}
                onSelect={onSelect}
                onRunEligibility={onRunEligibility}
                eligibilityLoading={evaluationLoading}
                disabled={busy}
                vin={vin}
              />
            </Segment>
          )}

          {/* Hidden once the guarantee exists, so the outcome message is the
              only state on screen and the force controls cannot be re-used. */}
          {!(forceResult && forceResult.success) && (eligibilityResults || evaluationErrorMessage) && (
            <Segment>
              {evaluationErrorMessage && (
                <Message negative>
                  <p>{evaluationErrorMessage}</p>
                </Message>
              )}
              {eligibilityResults && (
                <EligibilityResultsLog
                  results={eligibilityResults}
                  vin={evaluatedVin}
                  productName={productName}
                  disabled={busy}
                  createDisabled={!canForce}
                  onCreateGuarantee={onOpenModal}
                  onBack={onBack}
                />
              )}
            </Segment>
          )}

          {forceResult && (
            <Segment>
              <ForceResultMessage
                result={forceResult}
                disabled={busy}
                onReset={onReset}
              />
            </Segment>
          )}
        </Segment.Group>
      </Container>

      {/* Instructions, until the first search is performed */}
      {!hasSearched && (
        <div>
          <br />

          <ForceGuaranteeInstructions />
        </div>
      )}

      {/* Rendered only while open, so the justification it holds is created
          fresh for every force and can never carry over to the next order. */}
      {modalOpen && (
        <ForceConfirmationModal
          open
          loading={forceLoading}
          errorMessage={forceErrorMessage}
          orderId={evaluatedId}
          vin={evaluatedVin}
          productName={productName}
          failedRulesCount={failedRulesCount}
          totalRulesCount={totalRulesCount}
          onClose={onCloseModal}
          onConfirm={onConfirm}
        />
      )}
    </div>
  );
}

ForceGuaranteePage.propTypes = {
  products: PropTypes.array.isRequired,
  productsLoading: PropTypes.bool.isRequired,
  productsErrorMessage: PropTypes.string,
  transactions: PropTypes.array.isRequired,
  transactionsLoading: PropTypes.bool.isRequired,
  transactionsErrorMessage: PropTypes.string,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  productCode: PropTypes.string.isRequired,
  vin: PropTypes.string.isRequired,
  hasSearched: PropTypes.bool.isRequired,
  evaluatedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  eligibilityResults: PropTypes.array,
  evaluationLoading: PropTypes.bool.isRequired,
  evaluationErrorMessage: PropTypes.string,
  evaluatedVin: PropTypes.string,
  productName: PropTypes.string,
  failedRulesCount: PropTypes.number.isRequired,
  totalRulesCount: PropTypes.number.isRequired,
  forceResult: PropTypes.shape({
    success: PropTypes.bool,
    status: PropTypes.string,
    message: PropTypes.string,
  }),
  forceLoading: PropTypes.bool.isRequired,
  forceErrorMessage: PropTypes.string,
  modalOpen: PropTypes.bool.isRequired,
  busy: PropTypes.bool.isRequired,
  canForce: PropTypes.bool.isRequired,
  onSearch: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onRunEligibility: PropTypes.func.isRequired,
  onOpenModal: PropTypes.func.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

ForceGuaranteePage.defaultProps = {
  productsErrorMessage: null,
  transactionsErrorMessage: null,
  selectedId: null,
  evaluatedId: null,
  eligibilityResults: null,
  evaluationErrorMessage: null,
  evaluatedVin: '',
  productName: '',
  forceResult: null,
  forceErrorMessage: null,
};

export default ForceGuaranteePage;
